import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import ar from './ar'

// English/Arabic translation for the whole app.
//
// Keys are the English text itself: t('Save changes') returns the Arabic
// translation when Arabic is active, and the English text otherwise (so a
// missing translation degrades to English instead of a raw key). Values can
// use {placeholders}: t('Order {code} received', { code }).
//
// Three independent language choices share this module:
//  - admin:     each staff member's own choice (admin + owner panels, login)
//  - store:     the public storefront, set by the owner in Settings
//               (English, Arabic, or both with a switcher for customers)
//  - marketing: the landing / get-started pages, chosen by the visitor
// Only one scope renders at a time (they live on different routes), and it
// sets the module-level language below while rendering, so plain `t()`
// imports work anywhere - including in toasts fired from event handlers.

const DICTIONARIES = { ar }
export const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ar', label: 'العربية', short: 'ع' },
]

let currentLang = 'ar'

function interpolate(text, vars) {
  if (!vars) return text
  return text.replace(/\{(\w+)\}/g, (m, name) => (vars[name] != null ? String(vars[name]) : m))
}

export function t(text, vars) {
  if (text == null) return ''
  const key = String(text)
  const dict = DICTIONARIES[currentLang]
  const translated = dict && Object.prototype.hasOwnProperty.call(dict, key) ? dict[key] : key
  return interpolate(translated, vars)
}

export const getLanguage = () => currentLang
export const isRTL = (lang = currentLang) => lang === 'ar'
export const locale = (lang = currentLang) => (lang === 'ar' ? 'ar-u-nu-latn' : 'en-US')

// Locale-aware date/number helpers (Latin digits in Arabic, to match prices).
export const formatDate = (value, options = { dateStyle: 'medium' }) => {
  if (!value) return ''
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(locale(), options)
}
export const formatDateTime = (value, options = { dateStyle: 'medium', timeStyle: 'short' }) => {
  if (!value) return ''
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(locale(), options)
}
export const formatTime = (value, options = { hour: '2-digit', minute: '2-digit' }) => {
  if (!value) return ''
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(locale(), options)
}

const read = key => { try { return localStorage.getItem(key) } catch { return null } }
const write = (key, value) => { try { localStorage.setItem(key, value) } catch { /* storage unavailable */ } }
// Arabic is the product default. A language explicitly chosen and saved by a
// visitor or staff member still takes precedence in LanguageScope.
const browserLang = () => 'ar'
const valid = lang => (lang === 'ar' || lang === 'en' ? lang : null)

export const STORAGE_KEYS = { admin: 'posq_admin_lang', store: 'posq_store_lang', marketing: 'posq_site_lang' }

// Dev helper: in the browser console, window.__i18nScan() lists visible
// English text left on the current page (to spot untranslated strings).
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__i18nScan = () => {
    const out = new Set()
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT)
    let node
    while ((node = walker.nextNode())) {
      const text = node.nodeValue.trim()
      const el = node.parentElement
      if (!el || ['SCRIPT', 'STYLE', 'NOSCRIPT'].includes(el.tagName)) continue
      if (/[A-Za-z]{3,}/.test(text) && (el.offsetParent !== null || getComputedStyle(el).position === 'fixed')) out.add(text.slice(0, 90))
    }
    for (const el of document.querySelectorAll('[placeholder],[aria-label],[title]')) {
      for (const attr of ['placeholder', 'aria-label', 'title']) {
        const v = el.getAttribute(attr)
        if (v && /[A-Za-z]{3,}/.test(v)) out.add(`${attr}: ${v.slice(0, 90)}`)
      }
    }
    for (const o of document.querySelectorAll('option')) if (/[A-Za-z]{3,}/.test(o.textContent)) out.add(`option: ${o.textContent.trim().slice(0, 60)}`)
    return [...out]
  }
}

const I18nContext = createContext({ lang: 'ar', dir: 'rtl', t, setLang: () => {}, canSwitch: false, scope: 'admin' })

// Wraps a part of the app (admin, store, marketing) and decides its language.
// `fixed` forces one language (a store set to Arabic only); otherwise the
// visitor's saved choice or their browser language is used.
export function LanguageScope({ scope, fixed, fallback, children }) {
  const storageKey = STORAGE_KEYS[scope]
  const [chosen, setChosen] = useState(() => valid(read(storageKey)))
  const lang = valid(fixed) || chosen || valid(fallback) || browserLang()

  // Set synchronously so every child rendered below sees the right language.
  currentLang = lang

  useEffect(() => {
    const html = document.documentElement
    html.lang = lang
    html.dir = isRTL(lang) ? 'rtl' : 'ltr'
  }, [lang])

  const setLang = useCallback(next => {
    if (!valid(next)) return
    write(storageKey, next)
    setChosen(next)
  }, [storageKey])

  const value = useMemo(() => ({ lang, dir: isRTL(lang) ? 'rtl' : 'ltr', t, setLang, canSwitch: !fixed, scope }), [lang, setLang, fixed, scope])
  // Re-mount the subtree on a language switch so every text (including
  // values computed once in effects) is rendered again in the new language.
  return <I18nContext.Provider value={value}><div key={lang} className="contents" dir={value.dir}>{children}</div></I18nContext.Provider>
}

export function useI18n() {
  return useContext(I18nContext)
}

// Compact EN / ع toggle used in the admin header, login, landing and store.
export function LanguageSwitcher({ className = '', variant = 'pill' }) {
  const { lang, setLang, canSwitch } = useI18n()
  if (!canSwitch) return null
  if (variant === 'select') {
    return (
      <select aria-label={t('Language')} value={lang} onChange={e => setLang(e.target.value)} className={className}>
        {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
      </select>
    )
  }
  return (
    <div role="group" aria-label={t('Language')} className={`lang-switch ${className}`}>
      {LANGUAGES.map(l => (
        <button key={l.code} type="button" onClick={() => setLang(l.code)} aria-pressed={lang === l.code} lang={l.code}
          className={lang === l.code ? 'is-active' : ''}>{l.code === 'ar' ? 'عربي' : 'EN'}</button>
      ))}
    </div>
  )
}
